import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, X } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useEscapeKey } from '../../../app/hooks/useEscapeKey';
import { useAppStore } from '../../../app/hooks/useAppStore';
import type { Classroom, TeacherAccount } from '../../../accounts';
interface TransferClassroomModalProps {
  open: boolean;
  classroom: Classroom | null;
  activeAccount: TeacherAccount;
  onClose: () => void;
}
export function TransferClassroomModal({
  open,
  classroom,
  activeAccount,
  onClose
}: TransferClassroomModalProps) {
  const {
    accounts,
    transferClassroomOwnership
  } = useAppStore();
  const [selectedTeacherId, setSelectedTeacherId] = useState<string>('');
  const [keepAsCoTeacher, setKeepAsCoTeacher] = useState(true);
  useEscapeKey(onClose, open && classroom !== null);
  if (!classroom) return null;
  // Get co-teachers
  const coTeachers = (classroom.coTeacherIds || []).map(id => accounts.accountsById[id]).filter((a): a is TeacherAccount => a?.role === 'teacher');
  // Get school colleagues (excluding co-teachers and self)
  const schoolName = activeAccount.schoolName?.trim().toLowerCase();
  const schoolColleagues = Object.values(accounts.accountsById).filter((a): a is TeacherAccount => a.role === 'teacher' && a.id !== activeAccount.id && !classroom.coTeacherIds?.includes(a.id) && a.schoolName?.trim().toLowerCase() === schoolName && schoolName !== '' && schoolName !== undefined);
  const handleTransfer = () => {
    if (!selectedTeacherId) return;
    transferClassroomOwnership(activeAccount.id, classroom.id, selectedTeacherId, keepAsCoTeacher);
    onClose();
  };
  const selectedTeacher = accounts.accountsById[selectedTeacherId];
  return <AnimatePresence data-id="element-8441">
      {open && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="transfer-classroom-title" data-id="element-8442">
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} data-id="element-8443" />
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 10
      }} transition={{
        duration: 0.2
      }} className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-200" data-id="element-8444">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50" data-id="element-8445">
              <h2 id="transfer-classroom-title" className="text-lg font-bold text-slate-900" data-id="element-8446">
                Klas "{classroom.name}" overdragen
              </h2>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-full transition-colors" data-id="element-8447">
                <X className="w-5 h-5" data-id="element-8448" />
              </button>
            </div>

            <div className="p-6 space-y-6" data-id="element-8449">
              <div data-id="element-8450">
                <label className="block text-sm font-bold text-slate-900 mb-2" data-id="element-8451">
                  Wie wordt de nieuwe eigenaar?
                </label>
                <div className="space-y-2 max-h-48 overflow-y-auto pr-2 custom-scrollbar" data-id="element-8452">
                  {coTeachers.length === 0 && schoolColleagues.length === 0 && <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl text-sm text-slate-500 text-center" data-id="element-8453">
                      Er zijn geen co-docenten of collega's gevonden op jouw
                      school.
                      <br data-id="element-8454" />
                      Laat een collega eerst een account aanmaken met dezelfde
                      schoolnaam.
                    </div>}

                  {coTeachers.length > 0 && <div className="mb-4" data-id="element-8455">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8456">
                        Huidige co-docenten
                      </div>
                      {coTeachers.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-200'}`} data-id="element-8457">
                          <input type="radio" name="newOwner" value={t.id} checked={selectedTeacherId === t.id} onChange={e => setSelectedTeacherId(e.target.value)} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600" data-id="element-8458" />
                          <span className="text-sm font-medium text-slate-900" data-id="element-8459">
                            {t.displayName}
                          </span>
                        </label>)}
                    </div>}

                  {schoolColleagues.length > 0 && <div data-id="element-8460">
                      <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-8461">
                        Collega's op {schoolName}
                      </div>
                      {schoolColleagues.map(t => <label key={t.id} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${selectedTeacherId === t.id ? 'bg-indigo-50 border-indigo-200' : 'bg-white border-slate-200 hover:border-indigo-200'}`} data-id="element-8462">
                          <input type="radio" name="newOwner" value={t.id} checked={selectedTeacherId === t.id} onChange={e => setSelectedTeacherId(e.target.value)} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-600" data-id="element-8463" />
                          <span className="text-sm font-medium text-slate-900" data-id="element-8464">
                            {t.displayName}
                          </span>
                        </label>)}
                    </div>}
                </div>
              </div>

              {selectedTeacherId && <div className="p-4 bg-sky-50 border border-sky-100 rounded-xl space-y-3" data-id="element-8465">
                  <div className="flex items-start gap-2" data-id="element-8466">
                    <Info className="w-5 h-5 text-sky-600 flex-shrink-0 mt-0.5" data-id="element-8467" />
                    <div data-id="element-8468">
                      <p className="text-sm font-bold text-sky-900" data-id="element-8469">
                        Na overdracht:
                      </p>
                      <ul className="mt-1 space-y-1 text-sm text-sky-800 list-disc list-inside" data-id="element-8470">
                        <li data-id="element-8471">
                          <span className="font-bold" data-id="element-8472">
                            {selectedTeacher?.displayName}
                          </span>{' '}
                          wordt de nieuwe eigenaar.
                        </li>
                        <li data-id="element-8473">
                          Alle {classroom.learnerIds.length} leerlingen blijven
                          in de klas.
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="pt-3 border-t border-sky-200/50" data-id="element-8474">
                    <label className="flex items-start gap-3 cursor-pointer" data-id="element-8475">
                      <input type="checkbox" checked={keepAsCoTeacher} onChange={e => setKeepAsCoTeacher(e.target.checked)} className="mt-1 w-4 h-4 text-indigo-600 rounded border-sky-300 focus:ring-indigo-600" data-id="element-8476" />
                      <span className="text-sm text-sky-900" data-id="element-8477">
                        <span className="font-bold block" data-id="element-8478">
                          Blijf zelf co-docent
                        </span>
                        Je behoudt inzicht in de klas, maar kunt geen
                        klasinstellingen meer wijzigen.
                      </span>
                    </label>
                  </div>
                </div>}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100" data-id="element-8479">
                <Button onClick={onClose} variant="secondary" data-id="element-8480">
                  Annuleren
                </Button>
                <Button onClick={handleTransfer} disabled={!selectedTeacherId} className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-xl text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-8481">
                  Overdragen
                </Button>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}